import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Sanitizes error messages to ensure user-friendly messages are displayed.
 * Removes technical details like JSON objects, status codes, and internal error details.
 */
export function sanitizeErrorMessage(error: string | null | undefined): string {
  if (!error) {
    return "Something went wrong. Please try again.";
  }

  // Try to parse as JSON
  try {
    const parsed = JSON.parse(error);
    // If it's an object with an error property, try to extract a user-friendly message
    if (typeof parsed === "object" && parsed !== null) {
      if (parsed.error) {
        // Handle nested error objects
        if (typeof parsed.error === "object" && parsed.error.message) {
          // Check if the message is still technical
          const message = parsed.error.message;
          if (message.includes("Internal error") || message.includes("INTERNAL") || message.includes("500")) {
            return "Something went wrong. Please try again.";
          }
          return message;
        }
        if (typeof parsed.error === "string") {
          return parsed.error;
        }
      }
      // If it's a plain object, return generic message
      return "Something went wrong. Please try again.";
    }
  } catch {
    // Not JSON, continue with string processing
  }

  // Check for technical error patterns
  const technicalPatterns = [
    /Internal error/i,
    /INTERNAL/i,
    /500/i,
    /status.*500/i,
    /code.*500/i,
    /\{"error":/i, // JSON error objects
  ];

  for (const pattern of technicalPatterns) {
    if (pattern.test(error)) {
      return "Something went wrong. Please try again.";
    }
  }

  // If error looks like a JSON string, return generic message
  if (error.trim().startsWith("{") && error.trim().endsWith("}")) {
    return "Something went wrong. Please try again.";
  }

  // Return the error as-is if it seems user-friendly
  return error;
}
